/**
 * Amount Input Validator
 *
 * Unified validator for deposit and withdrawal amount inputs.
 * Replaces the duplicate DepositInputValidator and WithdrawalInputValidator.
 *
 * Uses shared modules:
 *   - field-validators.js   → validateAmount()
 *   - field-state-manager.js → applyFieldState / clearFieldState
 *
 * @package betbeast
 */

import { validateAmount } from './field-validators.js';
import {
    CLASSES,
    applyFieldState,
    clearFieldState,
    getOrCreateErrorElement
} from './field-state-manager.js';

export class AmountInputValidator {
    /**
     * @param {HTMLElement} modalElement       Modal root element.
     * @param {Object}      config
     * @param {string}      config.inputSelector    CSS selector for the amount <input>.
     * @param {string}      config.wrapperSelector  CSS selector for the wrapper that gets state classes.
     * @param {string}      config.emptyMessage     Message shown when the field is empty.
     * @param {string}      [config.label]          "deposit" or "withdrawal" — used in error messages.
     * @param {number}      [config.availableBalance] Balance for withdrawal cap check.
     */
    constructor(modalElement, config = {}) {
        this.modal = modalElement;
        this.amountInput = null;
        this.inputWrapper = null;
        this.config = Object.assign({
            inputSelector:    '.wallet-deposit-amount-input',
            wrapperSelector:  '.wallet-deposit-amount-input-wrapper',
            emptyMessage:     'Please enter a deposit amount',
            label:            'deposit',
            availableBalance: undefined
        }, config);

        if (!this.modal) {
            console.warn('Modal element not provided to AmountInputValidator');
            return;
        }

        this.init();
    }

    // ---- Initialisation -------------------------------------------------------

    init() {
        this.amountInput  = this.modal.querySelector(this.config.inputSelector);
        this.inputWrapper = this.modal.querySelector(this.config.wrapperSelector);

        if (!this.amountInput || !this.inputWrapper) {
            console.warn('Amount input or wrapper not found');
            return;
        }

        // Ensure an error element exists
        getOrCreateErrorElement(this.inputWrapper);

        // Wire listeners
        this.amountInput.addEventListener('blur', () => this.validateOnBlur());
        this.amountInput.addEventListener('input', () => this.clearError());
    }

    // ---- Validation -----------------------------------------------------------

    /**
     * Run validation on blur.
     */
    validateOnBlur() {
        var valueStr = this.amountInput.value.trim();

        if (!valueStr) {
            this.showError(this.config.emptyMessage);
            return;
        }

        var amount = parseFloat(valueStr);
        var result = this._runValidation(amount);

        if (!result.valid) {
            this.showError(result.message);
        } else {
            this.showSuccess();
        }
    }

    /**
     * Validate the current input value.
     * Can be called with or without an explicit amount (for backwards-compat
     * with WithdrawalSubmitHandler which called `validator.validateAmount()`
     * with no args).
     *
     * @param {number} [amount] Amount to validate (defaults to current input value).
     * @returns {{ isValid: boolean, error: string|undefined }}
     */
    validateAmount(amount) {
        if (amount === undefined) {
            amount = parseFloat(this.amountInput.value.trim());
        }

        var result = this._runValidation(amount);

        return result.valid
            ? { isValid: true }
            : { isValid: false, error: result.message };
    }

    /**
     * Validate before form submission — shows error UI if invalid.
     *
     * @returns {{ isValid: boolean, error: string|undefined }}
     */
    validateBeforeSubmit() {
        var valueStr = this.amountInput.value.trim();

        if (!valueStr) {
            this.showError(this.config.emptyMessage);
            return { isValid: false, error: this.config.emptyMessage };
        }

        var amount = parseFloat(valueStr);
        var result = this._runValidation(amount);

        if (!result.valid) {
            this.showError(result.message);
        }

        return result.valid
            ? { isValid: true }
            : { isValid: false, error: result.message };
    }

    // ---- UI state -------------------------------------------------------------

    /**
     * Show an error message on the amount wrapper.
     *
     * @param {string} message
     */
    showError(message) {
        applyFieldState(this.inputWrapper, {
            valid:   false,
            message: message,
            type:    'error'
        });
    }

    /**
     * Clear error / success state.
     */
    clearError() {
        clearFieldState(this.inputWrapper);
    }

    /**
     * Show the success (valid) state.
     */
    showSuccess() {
        applyFieldState(this.inputWrapper, {
            valid:   true,
            message: '',
            type:    'valid'
        });
    }

    /**
     * Update min/max limits (called when payment method changes).
     *
     * @param {number} min
     * @param {number} max
     */
    updateLimits(min, max) {
        this._applyBound('min', min);
        this._applyBound('max', max);

        // Re-validate if the input already has a value
        if (this.amountInput.value) {
            var amount = parseFloat(this.amountInput.value);
            var result = this._runValidation(amount);

            if (!result.valid) {
                this.showError(result.message);
            } else {
                this.clearError();
            }
        }
    }

    /**
     * Update available balance (withdrawal only).
     *
     * @param {number} balance
     */
    updateAvailableBalance(balance) {
        this.config.availableBalance = balance;
    }

    // ---- Private --------------------------------------------------------------

    /**
     * Set or clear one bound attribute.
     *
     * An unset bound removes the attribute rather than writing null: _runValidation()
     * then parses NaN for it, and `amount < NaN` is false, so the bound simply does
     * not apply. Writing null would stringify to "null" and break the comparison.
     *
     * @private
     * @param {string} attr - 'min' or 'max'
     * @param {number|null} value
     */
    _applyBound(attr, value) {
        if (value === null || value === undefined || value === '') {
            this.amountInput.removeAttribute(attr);
        } else {
            this.amountInput.setAttribute(attr, value);
        }
    }

    /**
     * Delegate to the shared pure validator.
     *
     * @private
     * @param {number} amount
     * @returns {{ valid: boolean, message: string, type: string }}
     */
    _runValidation(amount) {
        var min = parseFloat(this.amountInput.getAttribute('min'));
        var max = parseFloat(this.amountInput.getAttribute('max'));

        return validateAmount(amount, {
            min:              min,
            max:              max,
            availableBalance: this.config.availableBalance,
            label:            this.config.label
        });
    }
}
